function DataDeletionPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <article className="max-w-3xl mx-auto bg-white shadow rounded-lg p-6 sm:p-8">
        <header className="border-b border-gray-200 pb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Data Deletion Policy
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            <strong>IIMI HR</strong> — Digital Application &amp; HR Management System
          </p>
          <p className="mt-1 text-gray-500">
            This policy applies to the IIMI HR mobile app (candidate app) and its developer.
          </p>
        </header>

        <section className="mt-6">
          <h2 className="text-xl font-semibold text-gray-900">
            About IIMI HR
          </h2>
          <p className="mt-2 text-gray-700">
            IIMI HR is a digital application and HR management system. As a candidate you:
          </p>
          <ul className="mt-2 list-disc list-inside space-y-1 text-gray-700">
            <li>Log in with your <strong>phone number</strong> (OTP sent to your phone).</li>
            <li>Build your <strong>profile</strong>: basic info (name, email, date of birth, gender, education level, profile picture), identity (document number and identity document images), present and permanent address, emergency contact (name and phone), other info (e.g. father’s and mother’s name), and work info (employee ID, project name, branch, shift, reference).</li>
            <li>Upload <strong>documents</strong>: CV; education certificates (SSC and last degree); testimonial; My Verified file; commitment file; NDA (first and second page); and agreement (first and second page).</li>
            <li>Submit a <strong>job or engagement application</strong> and check your <strong>application status</strong> (e.g. applied, scheduled, selected, under review, submitted, or rejected).</li>
          </ul>
          <p className="mt-2 text-gray-700">
            The HR team reviews and manages applications through a separate admin system. This policy describes how you can request deletion of your account and associated data.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900">
            How to request deletion of your account and data
          </h2>
          <p className="mt-2 text-gray-700">
            You can request deletion in either of the following ways:
          </p>
          <ol className="mt-4 list-decimal list-inside space-y-3 text-gray-700">
            <li>
              <strong>From the app:</strong> Open the IIMI HR app and log in with your phone number (OTP). Go to <strong>Profile</strong> and use the option to <strong>Delete my account</strong> or <strong>Delete my profile</strong>. This removes your profile and all uploaded documents from our system. For full account deletion (including your phone number and application record), use the option below or contact us by email.
            </li>
            <li>
              <strong>By email:</strong> Send an email to the developer at the support address shown on the Google Play store listing for <strong>IIMI HR</strong>. Use the subject <strong>&quot;Account deletion request&quot;</strong> and include the <strong>phone number</strong> linked to your account (and email if you have one). We will process your request and delete your account and associated data within a reasonable time, typically within <strong>30 days</strong>.
            </li>
          </ol>
          <p className="mt-3 text-gray-600 text-sm">
            Your account is linked to your phone number; providing it allows us to find and delete the correct profile and data.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900">
            Data we delete
          </h2>
          <p className="mt-2 text-gray-700">
            When you request deletion, we delete the following data associated with your candidate account:
          </p>
          <ul className="mt-3 list-disc list-inside space-y-1 text-gray-700">
            <li><strong>Account:</strong> Phone number and verification-related data.</li>
            <li><strong>Basic profile:</strong> Full name, phone, email, date of birth, gender, education level, profile picture file.</li>
            <li><strong>Identity:</strong> Document number and all identity document images (e.g. national ID, passport, or birth registration — front/back).</li>
            <li><strong>Address:</strong> Present address (district, upazila, street) and permanent address (district, upazila, street).</li>
            <li><strong>Emergency contact:</strong> Name and phone number.</li>
            <li><strong>Other information:</strong> Father’s name, mother’s name, and any other such fields you provided.</li>
            <li><strong>Work information:</strong> Employee ID, project name, branch, shift, reference.</li>
            <li><strong>Uploaded documents:</strong> CV; education certificates (SSC certificate and last degree certificate); testimonial file; My Verified file; commitment file; NDA (first and second page files); agreement (first and second page files).</li>
            <li><strong>Application data:</strong> Your application record(s), including status, submission time, rejection reason (if any), admin notes, and remarks.</li>
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900">
            Data we may keep (and for how long)
          </h2>
          <p className="mt-2 text-gray-700">
            In a few cases we may still keep some data, but not in a way that identifies you personally:
          </p>
          <ul className="mt-3 list-disc list-inside space-y-2 text-gray-700">
            <li>
              <strong>Anonymized data:</strong> We may keep numbers or trends that no longer contain your name, phone, or any way to identify you (e.g. &quot;how many applications this month&quot;). We use this only to improve the app and recruitment process, not to track you.
            </li>
            <li>
              <strong>Backups (90 days):</strong> Our servers keep backup copies of data. When you request deletion, we remove your data from the main system right away. Any copy that was already in a backup will be deleted within <strong>90 days</strong>. After 90 days, your data is gone from backups too.
            </li>
            <li>
              <strong>Legal records:</strong> If the law requires us to keep certain records (e.g. for tax or employment), we keep only what is legally required and for the period required by law.
            </li>
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900">
            What happens after you request deletion
          </h2>
          <p className="mt-2 text-gray-700">
            <strong>Step 1:</strong> We process your request and remove your account and personal data from our main (active) system. You will no longer be able to log in and we will not use your data.
          </p>
          <p className="mt-2 text-gray-700">
            <strong>Step 2:</strong> If any copy of your data was stored in a backup, we delete it from those backups within <strong>90 days</strong>. After that, we do not keep your personal data anywhere for your IIMI HR account.
          </p>
        </section>

        <footer className="mt-10 pt-6 border-t border-gray-200 text-sm text-gray-500">
          <p>
            This policy applies to the <strong>IIMI HR</strong> mobile app and its developer. Last updated: March 2025.
          </p>
        </footer>
      </article>
    </div>
  );
}

export default DataDeletionPolicy;
